import * as React from "react";
import * as S from "@styled";
import { Link, Route, Switch, useHistory, useParams } from "react-router-dom";
import { Utilities } from "../../shared/utilities";
import { getEnvironments, setIsLoading } from "../actions/AppActions";
import { AppContext } from "../App";
import { IAgentGroupData, IAppReducerState, IEnvironment } from "../shared/model";
import { PageLoadSpinner } from "../../shared/components/PageLoadSpinner";
import styled, { css } from "styled-components";
import theme from "styled-theming";
import { ThemeType } from "shared/styles/theme";
import { EnvironmentListIds } from "../../shared/componentIds";

const AgentGroupLevelContent = React.lazy(() => import("./detailLevels/AgentGroupLevelContent"));
const AgentLevelContent = React.lazy(() => import("./detailLevels/agent/AgentLevelContent"));
const AgentLevelEditContent = React.lazy(() => import("./detailLevels/agent/AgentLevelEditContent"));
const EnvironmentLevelContent = React.lazy(() => import("./detailLevels/EnvironmentLevelContent"));

const EnvironmentList: React.FC = () => {
    const { state } = React.useContext<IAppReducerState>(AppContext);

    return (
        <S.IndentedContainer>
            <S.PageHeaderContainer>
                <h3>Administration /</h3>
                <h1>Environments</h1>
            </S.PageHeaderContainer>
            <S.SidePanelLayout>
                <S.SidePanelFlexContainer>
                    <S.SidePanelContainer>
                        <S.InputGroup>
                            <Link id={EnvironmentListIds.linkNewEnvironmentButton } to={"/NewEnvironment"}>
                                <S.Button>
                                    <S.ButtonIcon className="ci-plus" />New Environment
                                </S.Button>
                            </Link>
                        </S.InputGroup>
                        {!state?.environments.length ? null : <NonVirtualizedEnvironmentListContainer id={EnvironmentListIds.containerEnvironmentsList}>
                            {state.environments.map((env, idx) => (
                                <Route key={idx} path="/:environment">
                                    <StyledEnvironment
                                        env={env}
                                        idx={idx}
                                        isLast={state?.environments?.length === idx + 1}
                                        className="environments-list-item"
                                    />
                                </Route>
                            ))}
                        </NonVirtualizedEnvironmentListContainer>
                        }
                    </S.SidePanelContainer>
                    {!state?.environments.length ? null :
                        <S.ContentContainer>
                            <S.Panel>
                                <S.PanelBody style={{ minHeight: '270px' }}>
                                    <S.PanelPadding>
                                        <Switch>
                                            <Route exact={true} path="/NewEnvironment">
                                                <EnvironmentLevelContent isNew={true} />
                                            </Route>
                                            <Route exact={true} path="/:environment">
                                                <React.Suspense fallback={<PageLoadSpinner />}>
                                                    <EnvironmentLevelContent isNew={false} />
                                                </React.Suspense>
                                            </Route>
                                            <Route exact={true} path="/:environment/NewAgentGroup">
                                                <AgentGroupLevelContent isNew={true} />
                                            </Route>
                                            <Route exact={true} path="/:environment/:agentgroup">
                                                <React.Suspense fallback={<PageLoadSpinner />}>
                                                    <AgentGroupLevelContent isNew={false} />
                                                </React.Suspense>
                                            </Route>
                                            <Route exact={true} path="/:environment/:agentgroup/NewAgent">
                                                <AgentLevelEditContent isNew={true} />
                                            </Route>
                                            <Route exact={true} path="/:environment/:agentgroup/NewApiGatewayAgent">
                                                <AgentLevelEditContent isNew={true} isApiGatewayAgent={true} />
                                            </Route>
                                            <Route exact={true} path="/:environment/:agentgroup/:agent/edit">
                                                <React.Suspense fallback={<PageLoadSpinner />}>
                                                    <AgentLevelEditContent isNew={false} />
                                                </React.Suspense>
                                            </Route>
                                            <Route exact={true} path="/:environment/:agentgroup/:agent">
                                                <React.Suspense fallback={<PageLoadSpinner />}>
                                                    <AgentLevelContent />
                                                </React.Suspense>
                                            </Route>
                                        </Switch>
                                    </S.PanelPadding>
                                </S.PanelBody>
                            </S.Panel>
                        </S.ContentContainer>
                    }
                </S.SidePanelFlexContainer>
            </S.SidePanelLayout>
        </S.IndentedContainer>
    );
};

const NonVirtualizedEnvironmentListContainer = styled(S.NonVirtualizedListContainer)`
    border: 0;
    border-radius: 0;
`;

const StyledEnvironmentListItemActiveCss = css<S.IListItemProps>`
    border: 1px solid ${S.listItemBorderColorActive};

    ${props => !props.$isLast && css`
        border-bottom: 0;
    `}

    ${props => props.index !== 0 && !props.$isLast && css`
        border-bottom: 0;
    `}

    + div {
        border-top: 1px solid ${S.listItemBorderColorActive};
    }
`;

const EnvironmentListItem = styled(S.ListItem)`
    border: 1px solid ${S.listItemBorderColor};
    border-bottom: 0;

    /*First item*/
    ${props => props.index === 0 && css`
        border-radius: 25px 25px 0 0;
    `}

    /*Last item*/
    ${props => props.$isLast && css`
        border-bottom: 1px solid ${S.listItemBorderColor};
        border-radius: 0 0 25px 25px;  
    `}

    /*Only item*/
    ${props => props.index === 0 && props.$isLast && css`
        border-bottom: 1px solid ${S.listItemBorderColor};
        border-radius: 25px;  
    `}

    ${props => props.$isActive && css<S.IListItemProps>`

        ${StyledEnvironmentListItemActiveCss};

        > a {
            background-color: ${S.listItemBackgroundColorActive};
            border-bottom: 1px solid ${S.listItemBorderColorActive};
        }
    `}

    &:hover {
        ${StyledEnvironmentListItemActiveCss};
    }

    box-shadow: none;
    background-color: ${S.listItemBackgroundColor};
`;

const Environment: React.FC<{ env: IEnvironment, idx: number, isLast: boolean, className?: string }> = ({ env, idx, isLast, className }) => {
    const { environment } = useParams<{ environment: string }>();
    const isActive = environment?.toLowerCase() == env.name?.toLowerCase();
    return <EnvironmentListItem key={idx}
        index={idx}
        $asColumn={true}
        $isActive={isActive}
        $isLast={isLast}
        className={className}
    >
        <S.StyledLink to={`/${env.name}`}>
            <EnvironmentListItemTitle
                env={env}
            />
        </S.StyledLink>
        {isActive && (
            <AgentGroupListDiv>
                {env.agentGroups?.map((agentGroup, idnx) => (
                    <S.StyledLink key={idnx} to={`/${env.name}/${agentGroup.name}`}>
                        <Switch>
                            <Route path={`/${env.name}/:agentgroup?`}>
                                <AgentGroupListItem agentGroup={agentGroup} />
                            </Route>
                        </Switch>
                    </S.StyledLink>
                ))}
            </AgentGroupListDiv>
        )}
    </EnvironmentListItem>
};

const EnvironmentListItemTitle: React.FC<{ env: IEnvironment }> = ({ env }) => {
    const history = useHistory();

    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);

    const handleEnvironmentOrder = async (e: React.SyntheticEvent, moveDown: boolean) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLoading(dispatch, true);
        try {

            await Utilities.patchJsonAndParseResponse("api/environment/changeOrder", {
                id: env?.id,
                moveDown: moveDown
            });
            getEnvironments(dispatch);
        } catch (e) {
            console.error(e);
            Utilities.toastError(e);
        }
        setIsLoading(dispatch, false);
    };

    const handleEnvironmentRemoval = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const confirmation = confirm(`Are you sure you want to delete ${env?.displayName}?`);
        if (confirmation) {
            if (env?.agentGroups?.length === 0) {
                setIsLoading(dispatch, true);
                try {
                    await Utilities.deleteFetchAndParseResponse(`api/environment/${env.id}`);
                    getEnvironments(dispatch);
                    history.push(`/Default`)
                    Utilities.toastSuccess(`Environment ${env.displayName} deleted successfully.`);
                } catch (e) {
                    console.error(e);
                    Utilities.toastError(e);
                }
                setIsLoading(dispatch, false);
            } else {
                Utilities.toastError(FRENDS.Localizations.Environment_CouldNotRemove_HasAgentGroups);
            }
        }
    };

    return (
        <S.ListItemTitle>
            <S.FlexContainer asRow={true} justifySpaceBetween={true} style={{ margin: "15px 0 15px 0" }}>
                <span>{env?.displayName ?? env?.name}</span>
                <span>
                    {/* tslint:disable: jsx-no-lambda */}
                    {(env?.displayOrder !== 0) && <UpControlItemIcon className="ci-small_long_up" onClick={(e) => handleEnvironmentOrder(e, false)}></UpControlItemIcon>}
                    {(env?.displayOrder !== state.environments.length - 1) && <DownControlItemIcon className="ci-small_long_down" onClick={(e) => handleEnvironmentOrder(e, true)}></DownControlItemIcon>}
                    {(env?.name?.toLowerCase() !== "default") && <DeleteControlItemIcon className="ci-trash_full" onClick={(e) => handleEnvironmentRemoval(e)}></DeleteControlItemIcon>}
                </span>
            </S.FlexContainer>
        </S.ListItemTitle>
    );
};

const ControlItemIcon = styled.i`
    opacity: 0;
    &:hover {
        opacity: 1 !important;
  }
`;

const UpControlItemIcon = styled(ControlItemIcon)``;
const DownControlItemIcon = styled(ControlItemIcon)``;
const DeleteControlItemIcon = styled(ControlItemIcon)``;

const StyledEnvironment = styled(Environment)`
    &:hover {
        ${ControlItemIcon} {
            opacity: 0.5;
        }
    }
`;

const agentGroupListBackgroundColor = theme("mode", {
    [ThemeType.light]: S.Colors.transparent,
    [ThemeType.dark]: S.Colors.dark2
})

const AgentGroupListDiv = styled.div`
    padding: 13px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: ${agentGroupListBackgroundColor};
`;

export const AgentGroupListItemTitleColorActive = theme("mode", {
    [ThemeType.light]: S.Colors.purplePrimary,
    [ThemeType.dark]: S.Colors.white,
});

export const AgentGroupListItemBackgroundColorActive = theme("mode", {
    [ThemeType.light]: S.Colors.purpleBackground,
    [ThemeType.dark]: S.Colors.dark
});

export const AgentGroupListItemInfoColor = theme("mode", {
    [ThemeType.light]: S.Colors.grey_1,
    [ThemeType.dark]: S.Colors.grey_2,
});

const AgentGroupListItemDiv = styled.div<{ active: boolean }>`
    padding: 8px 24px 8px 32px;

    &:hover {
        background-color: ${AgentGroupListItemBackgroundColorActive};
    }

    ${props => props.active && css`
        background-color: ${AgentGroupListItemBackgroundColorActive};
    `}

    > h4 {
        margin-bottom: 8px;
        margin-top: 0px;
        overflow: hidden;
        text-overflow: ellipsis;
        ${props => props.active && css`
            color:${AgentGroupListItemTitleColorActive}
        `}
    }
    
    > div {
        font-size: var(--small-text-font-size);
        color: ${AgentGroupListItemInfoColor}
    }
`;

const AgentGroupListItem: React.FC<{ agentGroup: IAgentGroupData, className?: string }> = ({ agentGroup, className }) => {
    const { agentgroup } = useParams<{ agentgroup: string }>();
    return (
        <AgentGroupListItemDiv
            className={className}
            active={agentgroup?.toLowerCase() === agentGroup.name?.toLowerCase()}
        >
            <h4>{agentGroup.displayName}</h4>
            <div>{FRENDS.Localizations.Agents}: {agentGroup.executionAgents?.length}</div>
            <div>{FRENDS.Localizations.GatewayAgents}: {agentGroup.gatewayAgents?.length}</div>
        </AgentGroupListItemDiv>
    );
};

export default EnvironmentList;
